import { toInteger } from 'lodash';
import { createCanvas, loadImageAsync } from '@/utils/file-kit';
import { Processor } from '@/utils/processor';
import { type IMediaLayerProps } from '@/version-types';
import { GIF_IDENTIFIER_ICON, WEBP_IDENTIFIER_ICON } from '../assets';
import { type FullFrame } from '../utils/gif';
import { identifyAnimatedImage, type AnimatedImageFormat } from './animated-image-decoder';
import { ICON_VISIBILITY_THRESHOLD } from './constants';
import { type GIFProcessor } from './gif-worker';
import gifWorkerUrl from './gif-worker?worker&url';
import { type EdgelessMediaLayer } from './index';
import { type IEdgelessMediaLayer, type RenderTarget } from './types';

const processor = new Processor();
try {
    const worker = new Worker(new URL('./gif-worker', import.meta.url), { type: 'module' });

    worker.addEventListener('error', () => {
        console.log('worker.onerror', gifWorkerUrl);
        processor.clearWorker();
        processor.setUrl(gifWorkerUrl);
    });
    processor.setWorker(worker);
} catch (_e) {
    processor.setUrl(gifWorkerUrl); // 非同源报错时的兜底处理
}

export class EdgelessGIFElement implements IEdgelessMediaLayer {
    private static iconImages = new Map<AnimatedImageFormat, Promise<HTMLImageElement>>();
    private format: AnimatedImageFormat | undefined;
    private iconImage: HTMLImageElement | undefined;
    private sourcePromise: Promise<{ buffer: ArrayBuffer; format: AnimatedImageFormat }> | null = null;
    private framesPromise: Promise<FullFrame[]> | null = null;
    private resourceVersion = 0;
    private playVersion = 0;
    private disposed = false;
    private sourceController: AbortController | null = null;

    declare private gifCover: HTMLImageElement | HTMLCanvasElement;
    private renderTarget: RenderTarget = 'cover';
    private gifFrames: FullFrame[] = [];
    private gifFrameIndex: number = 0;
    private frameCanvas: HTMLCanvasElement | null = null;
    private frameCtx: CanvasRenderingContext2D | null = null;
    private currentTime = 0;

    constructor(private object: EdgelessMediaLayer) {}

    /**
     * 提供已从素材内容识别的格式，供图层管理展示名称
     * @returns 已识别格式，资源尚未加载时为 undefined
     */
    getFormat(): AnimatedImageFormat | undefined {
        return this.format;
    }

    async play() {
        if (this.disposed) return;
        const version = ++this.playVersion;
        try {
            const frames = await this.loadFrames();
            if (this.disposed || version !== this.playVersion) return;
            this.gifFrames = frames;
            this.renderTarget = 'frame';
            this.gifFrameIndex = 0;
            this.currentTime = 0;
            const { canvas, ctx } = createCanvas(this.object.width, this.object.height);
            this.frameCanvas = canvas;
            this.frameCtx = ctx;
            this.resetResource();
            // 静态 WebP 只需绘制一次，避免启动无意义的动画刷新。
            if (frames.length > 1) this.object.reportPlay();
            this.requestRender();
        } catch (error) {
            if (this.disposed || version !== this.playVersion) return;
            this.reset();
            this.requestRender();
            throw error;
        }
    }

    private requestRender() {
        const { edgelessCanvas, frame } = this.object.service;
        const frameId = frame.getLayerFrameId(this.object.layerId);
        if (typeof frameId === 'number') edgelessCanvas.addDirtyFrameId(frameId);
        edgelessCanvas.requestRenderAll();
    }

    getDurationAndUrl() {
        return {
            duration: 0,
            url: '',
        };
    }

    pause() {
        this.reset();
    }

    reset() {
        this.playVersion++;
        this.framesPromise = null;
        this.setRenderCover();
        this.frameCanvas = null;
        this.frameCtx = null;
        this.gifFrameIndex = 0;
        this.currentTime = 0;
        this.gifFrames = [];
        this.resetResource();
        this.object.reportPause();
    }

    seek(_time: number) {
        this.reset();
    }

    mute(_muted: boolean) {}

    async loadResource(_showIdentifier = true) {
        await this.loadCover();

        this.resetResource();
    }

    /** 替换 GIF / WebP 素材时清除旧请求和播放帧 */
    async reloadResource() {
        this.reset();
        this.resourceVersion++;
        this.sourceController?.abort();
        this.sourcePromise = null;
        this.format = undefined;
        this.framesPromise = null;
        this.iconImage = undefined;
        this.gifCover = undefined!;
        this.gifFrames = [];
        this.gifFrameIndex = 0;
        this.currentTime = 0;
        this.frameCanvas = null;
        this.frameCtx = null;
        this.setRenderCover();

        await this.loadResource();
    }

    setRenderCover() {
        this.renderTarget = 'cover';
    }

    /**
     * 读取带格式角标的封面，供导航预览使用，不切换播放状态
     * @returns 已加载的 GIF / WebP 封面
     */
    getPreviewCover() {
        return this.gifCover;
    }

    resetResource() {
        if (this.renderTarget === 'cover' || this.gifFrames.length === 0) {
            this.object.resource = this.gifCover;
        } else {
            const now = Date.now();
            let frame = this.gifFrames[this.gifFrameIndex];
            if (!this.currentTime) {
                this.currentTime = now;
            }
            if (now - this.currentTime > frame.delay) {
                this.gifFrameIndex = (this.gifFrameIndex + 1) % this.gifFrames.length;
                frame = this.gifFrames[this.gifFrameIndex];
                this.currentTime = Date.now();
            }
            const dims = frame.dims;
            const imageData = new ImageData(frame.data as ImageDataArray, dims.width, dims.height);
            if (this.frameCanvas && this.frameCtx) {
                this.frameCanvas.width = dims.width;
                this.frameCanvas.height = dims.height;
                this.frameCtx.clearRect(0, 0, dims.width, dims.height);
                this.frameCtx.putImageData(imageData, 0, 0);
                this.object.drawAiWatermark(this.frameCanvas);
                this.object.resource = this.frameCanvas;
            }
        }
    }

    dispose() {
        this.disposed = true;
        this.playVersion++;
        this.resourceVersion++;
        this.sourceController?.abort();
        this.sourceController = null;
        this.sourcePromise = null;
        this.framesPromise = null;
        this.gifCover = undefined!;
        this.iconImage = undefined;
        this.gifFrames = [];
        this.frameCanvas = null;
        this.frameCtx = null;
    }

    onLoop(cb: () => void | boolean) {}

    private loadSource() {
        if (!this.sourcePromise) {
            const props = this.object.service.flatLayer.getLayer(this.object.layerId).data as IMediaLayerProps;
            this.sourceController = new AbortController();
            const pending = fetch(props.url, { signal: this.sourceController.signal }).then(async (response) => {
                if (!response.ok) throw new Error(`Animated image request failed: ${response.status}`);
                const buffer = await response.arrayBuffer();
                return { buffer, format: identifyAnimatedImage(buffer) };
            });
            this.sourcePromise = pending;
            void pending.catch(() => {
                if (this.sourcePromise === pending) this.sourcePromise = null;
            });
        }
        return this.sourcePromise;
    }

    private async loadIdentifier(format: AnimatedImageFormat) {
        let image = EdgelessGIFElement.iconImages.get(format);
        if (!image) {
            image = loadImageAsync(format === 'webp' ? WEBP_IDENTIFIER_ICON.src : GIF_IDENTIFIER_ICON.src, false);
            EdgelessGIFElement.iconImages.set(format, image);
        }
        return image;
    }

    private loadFrames() {
        if (!this.framesPromise) {
            const version = this.resourceVersion;
            this.framesPromise = this.loadSource().then(async ({ buffer, format }) => {
                const icon = await this.loadIdentifier(format);
                const worker = await processor.get<typeof GIFProcessor>();
                const frames = await worker.decodeImage(buffer);
                if (!this.disposed && version === this.resourceVersion) {
                    this.format = format;
                    this.iconImage = icon;
                }
                return frames;
            });
        }
        return this.framesPromise;
    }

    private drawIdentifier(ctx: CanvasRenderingContext2D, width: number, height: number) {
        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        if (!this.iconImage || layer.width <= ICON_VISIBILITY_THRESHOLD || layer.height <= ICON_VISIBILITY_THRESHOLD)
            return;
        const icon = this.format === 'webp' ? WEBP_IDENTIFIER_ICON : GIF_IDENTIFIER_ICON;
        const iconWidth = icon.sizeRatio * Math.max(width, height);
        this.renderIdentifierIcon(
            ctx,
            this.iconImage,
            iconWidth,
            (icon.height / icon.width) * iconWidth,
            width,
            icon.paddingRatio * Math.max(width, height),
        );
    }

    private async loadCover() {
        const version = this.resourceVersion;

        const layer = this.object.service.flatLayer.getLayer(this.object.layerId);
        const props = layer.data as IMediaLayerProps;
        const url = props.poster || props.url;

        // 即使原生解码器不可用，也能依据真实文件格式展示正确的封面角标。
        const [image, source] = await Promise.all([loadImageAsync(url, false), this.loadSource().catch(() => null)]);
        const format = source?.format ?? (/\.webp(?:[?#]|$)/i.test(props.url) ? 'webp' : 'gif');
        const iconImage = await this.loadIdentifier(format);
        if (this.disposed || version !== this.resourceVersion) return;
        this.format = format;
        this.iconImage = iconImage;
        let cover: HTMLCanvasElement | HTMLImageElement = image;

        if (layer.width > ICON_VISIBILITY_THRESHOLD && layer.height > ICON_VISIBILITY_THRESHOLD) {
            const scale = this.object.getResourceScale({
                naturalWidth: cover.width,
                naturalHeight: cover.height,
            });
            const width = toInteger(layer.width * scale);
            const height = toInteger(layer.height * scale);
            const { canvas, ctx } = createCanvas(width, height, cover);
            this.drawIdentifier(ctx, width, height);
            cover = canvas;
            await this.object.drawAiWatermarkAsync(cover);
        }

        if (this.disposed || version !== this.resourceVersion) return;
        this.gifCover = cover;
    }

    private renderIdentifierIcon(
        ctx: CanvasRenderingContext2D,
        icon: HTMLImageElement,
        iconWidth: number,
        iconHeight: number,
        canvasWidth: number,
        padding: number,
    ) {
        const x = canvasWidth - iconWidth * (2 / 3) - padding;
        const y = -padding;

        ctx.drawImage(icon, x, y, iconWidth, iconHeight);
    }
}
